'use client'

import { Component, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
}

// Sits above the whole router tree. Without this, any render error thrown
// by a lazy-loaded page (including a stale chunk left over from a prior
// deploy — see the `vite:preloadError` handler in main.tsx) unmounts the
// entire React tree with nothing rendered in its place: a blank page that
// only a manual reload fixes, and no console-visible UI hint that anything
// went wrong.
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: unknown) {
    console.error('Unhandled render error:', error)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-4 bg-black px-4 text-center">
          <p className="text-white/70">Something went wrong loading this page.</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md bg-white px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-white/90"
          >
            Reload
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
